import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useDispatch, useSelector } from 'react-redux'
import { Button, FormControl, MenuItem, Select } from '@mui/material'
import { COLORS } from 'src/utils/Themes/colors'
import { POLICY_TYPE_ALL } from 'src/utils/constants/common'
import {
  setYear,
  setPolicyType,
  setSubType,
  resetFilters,
  setSelectedInsurance,
  setClearState,
} from 'src/actions/index'

const ExpiredPolicyToolbar = ({
  years,
  insurance,
  expired,
  setExpiredFilterParams,
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const expiredFilterParams = useSelector((state) => state?.expiredFilterParams)

  const handleYearChange = (event) => {
    dispatch(setYear(event?.target?.value))
  }

  const handleInsuranceChange = (event) => {
    const selectedInsurance = event?.target?.value
    const selectedInsuranceObject = insurance?.find(
      (ins) => ins?.insuranceType === selectedInsurance
    )
    dispatch(setSelectedInsurance(selectedInsuranceObject))
    dispatch(setPolicyType(selectedInsurance))
    dispatch(setSubType(''))
  }

  const handleSubtypeChange = (event) => {
    dispatch(setSubType(event?.target?.value))
  }

  const handleClear = () => {
    dispatch(resetFilters())
    setExpiredFilterParams((prevState) => ({
      ...prevState,
      year: years?.[0],
      policyType: POLICY_TYPE_ALL,
      subType: '',
    }))
  }

  const handleSubmit = () => {
    setExpiredFilterParams((prevState) => ({
      ...prevState,
      year: expiredFilterParams?.year,
      policyType: expiredFilterParams?.insuranceType?.policyType,
      subType: expiredFilterParams?.subType,
    }))
    dispatch(setClearState(true))
  }

  return (
    <div className={classes.toolbarContainer}>
      {expired && (
        <>
          <FormControl
            variant='outlined'
            size='small'
            className={classes.formControl}
          >
            <Select
              value={expiredFilterParams?.year || years?.[0]}
              onChange={handleYearChange}
              displayEmpty
              className={classes.select}
              MenuProps={{ disableScrollLock: false }}
            >
              <MenuItem disabled>Year</MenuItem>
              {years?.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            variant='outlined'
            size='small'
            className={classes.formControl}
          >
            <Select
              value={expiredFilterParams?.insuranceType?.policyType}
              onChange={handleInsuranceChange}
              displayEmpty
              className={classes.select}
              MenuProps={{ disableScrollLock: false }}
            >
              <MenuItem value={POLICY_TYPE_ALL}>
                <span className={classes.initialMenuItem}>
                  Policy Type :&nbsp;
                </span>{' '}
                All
              </MenuItem>
              {insurance?.map((ins, index) => (
                <MenuItem key={index} value={ins?.insuranceType}>
                  {ins?.insuranceType}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {expiredFilterParams?.selectedInsurance?.subType && (
            <FormControl
              variant='outlined'
              size='small'
              className={classes.formControl}
            >
              <Select
                value={expiredFilterParams?.subType || ''}
                onChange={handleSubtypeChange}
                displayEmpty
                className={classes.select}
                MenuProps={{ disableScrollLock: false }}
              >
                <MenuItem value='' disabled>
                  Subtype
                </MenuItem>
                {insurance
                  ?.find(
                    (ins) =>
                      ins?.insuranceType ===
                      expiredFilterParams?.insuranceType?.policyType
                  )
                  ?.subType?.map((subType) => (
                    <MenuItem key={subType} value={subType}>
                      {subType}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          <Button
            variant='contained'
            onClick={handleSubmit}
            size='small'
            className={`${classes.actionButton} ${classes.applyButton}`}
          >
            Apply
          </Button>

          {expiredFilterParams?.clearState && (
            <Button
              variant='contained'
              onClick={handleClear}
              size='small'
              className={`${classes.actionButton} ${classes.applyButton}`}
            >
              Clear
            </Button>
          )}
        </>
      )}
    </div>
  )
}

export default ExpiredPolicyToolbar

const useStyles = makeStyles({
  select: {
    borderRadius: '20px !important',
    border: `1px solid ${COLORS.DARK_PINK}`,
    fontSize: '13px',
    fontWeight: '400',
    textAlign: 'right',
    color: `${COLORS.DARK_PINK} !important`,
    '&:hover': {
      border: `1px solid ${COLORS.DARK_PINK}`,
    },
    '&.Mui-focused': {
      border: `1px solid ${COLORS.DARK_PINK}`,
      outline: `1px solid ${COLORS.DARK_PINK}`,
    },
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingRight: '24px',
    },
    '&:focus': {
      outline: 'none',
    },
    '& .css-jedpe8-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input':
      {
        padding: '6.5px 10px',
      },
    boxShadow: 'none',
    '&.MuiOutlinedInput-notchedOutline': { border: 0 },
    '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      border: 0,
    },
    '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 0,
    },
  },
  icon: {
    color: '#d32f2f',
  },
  menuItem: {
    textAlign: 'center !important',
  },
  inputLabel: {
    marginTop: '15px',
    marginRight: '10px',
    color: 'black !important',
    fontWeight: '400',
    fontSize: '18px',
    letterSpacing: '1px',
  },
  actionButton: {
    borderRadius: '20px !important',
    height: '35px',
    textTransform: 'capitalize !important',
  },
  applyButton: {
    backgroundColor: `${COLORS.DARK_PINK} !important`,
    fontWeight: '400',
    '&:hover': {
      backgroundColor: `${COLORS.DARK_PINK} !important`,
    },
    color: COLORS.WHITE,
  },
  toolbarContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '15px',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: '20px',
  },
  initialMenuItem: {
    color: 'black',
  },
})
